<template>
  <b-modal
    ref="notesModal"
    size="xl"
    hide-footer
    modal-class="app-notes-modal"
    @hide="onHide"
    @hidden="onHidden"
  >
    <template #modal-title>
      <div class="app-notes-modal-title">
        <span class="app-notes-modal-title-label">{{ $t("notes-and-history") }}</span>
        <template v-if="analysis">
          <span class="app-notes-modal-title-separator" aria-hidden="true"> — </span>
          <a :href="analysisHref" class="app-notes-modal-analysis-link" @click="onAnalysisClick">{{ analysis.name }}</a>
        </template>
        <a
          v-if="analysis && analysis.plant"
          :href="plantHref"
          class="app-button btn btn-secondary btn-xs app-notes-modal-title-plant-btn"
          @click="onPlantClick"
        >
          <app-icon icon="geo-alt" class="app-button-icon" />
          <span class="app-button-content has-icon">{{ $t("plant") }}</span>
        </a>
      </div>
    </template>

    <template v-if="analysis">
      <app-analysis-notes-and-history
        ref="notesAndHistory"
        :key="analysis.id"
        :analysis="analysis"
        :hide-title="true"
      >
        <template #toolbar>
          <div class="app-notes-modal-roles-bar">
            <app-analysis-roles-avatars
              :f2c-user="analysis.f2c_user || null"
              :ops-user="analysis.ops_user || null"
              :volateq-users="volateqUsers"
              :editable="canEditRoles"
              :loading="roleLoading"
              inline
              @change="onRoleChange"
            />
          </div>
        </template>
      </app-analysis-notes-and-history>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { Component, Ref } from "vue-property-decorator";
import { BaseAuthOnlineComponent } from "@/app/shared/components/base-auth-component/base-auth-online-component";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import { AnalysisSchema } from "@/app/shared/services/volateq-api/api-schemas/analysis-schema";
import { SimpleUserRoleSchema } from "@/app/shared/services/volateq-api/api-schemas/user-schemas";
import { notesModalService } from "@/app/shared/services/notes-modal-service";
import type { UnsavedComposerDraft } from "@/app/shared/services/helper/analysis-notes-helper";
import { AnalysisRolesHelper } from "@/app/shared/services/helper/analysis-roles-helper";
import { analysisEventService } from "@/app/analysis/shared/analysis-event-service";
import { AnalysisEvent } from "@/app/analysis/shared/types";
import AppAnalysisNotesAndHistory from "@/app/analysis/edit-analysis/edit-analysis-admin/analysis-notes-and-history.vue";
import AppAnalysisRolesAvatars, { AnalysisRolesChange } from "@/app/shared/components/app-user-role-avatar/analysis-roles-avatars.vue";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import { RouteQueryHelper } from "@/app/plant/shared/helper/route-query-helper";

interface INotesModal {
  show(): void;
  hide(): void;
}

interface IAppAnalysisNotesAndHistory {
  unsavedDraft: UnsavedComposerDraft | null;
}

/** Confirmation shown per composer whose unsent content would be discarded. */
const DISCARD_CONFIRM_KEYS: Record<UnsavedComposerDraft, string> = {
  note: "note-discard-confirm",
  whatsapp: "whatsapp-discard-confirm",
};

/** Subset of bootstrap-vue's modal hide event needed to keep the modal open. */
interface ModalHideEvent {
  trigger: string | null;
  preventDefault(): void;
}

/** Close triggers that discard the composer draft and therefore need a confirmation. */
const DISCARD_CONFIRM_TRIGGERS = ["backdrop", "esc", "headerclose"];

@Component({
  name: "app-notes-modal",
  components: {
    AppAnalysisNotesAndHistory,
    AppAnalysisRolesAvatars,
    AppIcon,
  },
})
export default class AppNotesModal extends BaseAuthOnlineComponent {
  @Ref() notesModal!: INotesModal;
  @Ref() notesAndHistory!: IAppAnalysisNotesAndHistory;

  analysis: AnalysisSchema | null = null;
  volateqUsers: SimpleUserRoleSchema[] = [];
  roleLoading = false;

  private unsubscribe: (() => void) | null = null;

  created() {
    this.unsubscribe = notesModalService.subscribe(analysisId => this.onOpen(analysisId));
  }

  beforeDestroy() {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  get analysisHref(): string {
    return this.analysis ? `/analysis/${this.analysis.id}` : "#";
  }

  get plantHref(): string {
    return this.analysis?.plant ? `/plant/${this.analysis.plant.id}?result=${this.analysis.id}` : "#";
  }

  get canEditRoles(): boolean {
    return this.hasPerm(this.p.READ_VOLATEQ_USER);
  }

  @CatchError()
  async onOpen(analysisId: string) {
    this.analysis = await this.volateqApi.getAnalysis(analysisId);
    this.volateqUsers = this.canEditRoles ? await this.volateqApi.getVolateqUsers() : [];
    await this.$nextTick();
    this.notesModal.show();
  }

  @CatchError("roleLoading")
  async onRoleChange(change: AnalysisRolesChange) {
    if (!this.analysis) {
      return;
    }

    await this.volateqApi.assignAnalysisRole(this.analysis.id, { role: change.role, user_id: change.userId });

    const selectedUser = AnalysisRolesHelper.resolveUser(change.userId, this.volateqUsers);
    if (change.role === "f2c") {
      this.analysis.f2c_user = selectedUser;
    } else {
      this.analysis.ops_user = selectedUser;
    }

    analysisEventService.emit(this.analysis.id, AnalysisEvent.UPDATE_ANALYSIS);
  }

  /** Closing via backdrop, X or ESC must not silently throw away a written note. */
  @CatchError()
  onHide(event: ModalHideEvent) {
    if (!DISCARD_CONFIRM_TRIGGERS.includes(event.trigger || "")) {
      return;
    }

    if (!this.confirmDiscardDraft()) {
      event.preventDefault();
    }
  }

  @CatchError()
  onHidden() {
    this.analysis = null;
    this.volateqUsers = [];
  }

  @CatchError()
  onAnalysisClick(event: MouseEvent) {
    // let the browser handle ctrl/cmd/shift-click (new tab/window); middle click never reaches @click
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    if (!this.confirmDiscardDraft()) {
      return;
    }

    this.notesModal.hide();
    this.$router.push(this.analysisHref).catch(() => undefined);
  }

  @CatchError()
  onPlantClick(event: MouseEvent) {
    // let the browser handle ctrl/cmd/shift-click (new tab/window); middle click never reaches @click
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    if (!this.confirmDiscardDraft()) {
      return;
    }

    this.notesModal.hide();
    // Same plant, other analysis: the plant view is reused, so tell it to select the new "result"
    this.$router.push(this.plantHref).then(() => RouteQueryHelper.emitQueryChanged()).catch(() => undefined);
  }

  /** Returns whether closing may proceed: either nothing is written, or the user confirmed. */
  private confirmDiscardDraft(): boolean {
    const unsavedDraft = this.notesAndHistory?.unsavedDraft;
    if (!unsavedDraft) {
      return true;
    }

    return confirm(this.$t(DISCARD_CONFIRM_KEYS[unsavedDraft]).toString());
  }
}
</script>

<style lang="scss">
.app-notes-modal {
  .modal-header {
    align-items: flex-start;
  }

  .modal-title {
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 0.5rem;
  }

  .close {
    flex-shrink: 0;
  }

  &-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.5rem;
    overflow-wrap: anywhere;
    word-break: break-word;

    &-label,
    &-plant-btn {
      flex-shrink: 0;
    }
  }

  &-roles-bar {
    display: flex;
    align-items: center;
  }

  &-analysis-link {
    color: var(--primary-bg-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

}
</style>
